import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Zap, Waves, Shield, GraduationCap,
  BarChart3, Sparkles, History, ShieldCheck, BookOpen,
  UploadCloud, FileText, MessageSquare,
  ChevronRight, ChevronLeft, X, Check,
} from "lucide-react";
import { api } from "@/lib/api";

const LS_KEY = "anatradex-onboarding-completed";
const LS_FAVS = "qtr-fav-watchlist";
const LS_PROFILE = "anatradex-trader-profile";

/**
 * 4-step onboarding wizard shown on first Dashboard visit — the SINGLE
 * onboarding sequence (the old 5-step OnboardingTour has been removed).
 *
 * Steps:
 *   1. Trader profile → adjusts default timeframe + risk preset
 *   2. Favorite assets → auto-fills the floating watchlist
 *   3. Feature tour → 5 hero cards linking to key surfaces
 *   4. Tester mission → import trades → generate report → read the guide
 *
 * Sequencing: only shown AFTER the risk-ack modal is accepted; the cookie
 * banner hides itself while the wizard is open (window events).
 */

export const TRADER_PROFILES = [
  {
    id: "scalper",
    label: "Scalpeur",
    subtitle: "Sessions courtes, M1-M15, exécution rapide",
    Icon: Zap,
    tint: "text-amber-400 border-amber-500/40 bg-amber-500/10",
    defaults: { timeframe: "M5", killZones: true, favSet: "fx-major" },
  },
  {
    id: "swing",
    label: "Swing trader",
    subtitle: "Positions H1-D1, analyse structurelle",
    Icon: Waves,
    tint: "text-accent-blue border-accent-blue/40 bg-accent-blue/10",
    defaults: { timeframe: "H4", killZones: false, favSet: "multi-asset" },
  },
  {
    id: "prop",
    label: "Prop firm",
    subtitle: "Règles strictes, kill-switch, audit continu",
    Icon: Shield,
    tint: "text-emerald-400 border-emerald-500/40 bg-emerald-500/10",
    defaults: { timeframe: "H1", killZones: true, favSet: "fx-major" },
  },
  {
    id: "beginner",
    label: "Débutant",
    subtitle: "Apprentissage progressif, valeurs par défaut sûres",
    Icon: GraduationCap,
    tint: "text-purple-400 border-purple-500/40 bg-purple-500/10",
    defaults: { timeframe: "D1", killZones: false, favSet: "starter" },
  },
];

const ASSET_CATEGORIES = [
  {
    id: "fx-major",
    label: "Forex Majeurs",
    symbols: ["EURUSD", "GBPUSD", "USDJPY", "USDCHF", "AUDUSD", "USDCAD", "NZDUSD"],
  },
  {
    id: "crypto",
    label: "Crypto",
    symbols: ["BTCUSD", "ETHUSD", "SOLUSD", "XRPUSD", "ADAUSD"],
  },
  {
    id: "metals",
    label: "Métaux & Énergie",
    symbols: ["XAUUSD", "XAGUSD", "WTI", "BRENT"],
  },
  {
    id: "stocks",
    label: "Actions US",
    symbols: ["AAPL", "TSLA", "NVDA", "META", "AMZN", "GOOGL", "MSFT"],
  },
  {
    id: "etf",
    label: "ETF",
    symbols: ["SPY", "QQQ", "GLD", "TLT", "IWM"],
  },
];

const FEATURE_TOUR = [
  { Icon: BarChart3, label: "Chart professionnel", desc: "26 indicateurs, 20+ outils de dessin, détection auto OB/FVG/CISD/CRT", to: "/app" },
  { Icon: Sparkles,  label: "Analyzer prédictif",   desc: "Score confiance passée / actuelle / future avec fourchette probabiliste", to: "/app" },
  { Icon: History,   label: "Backtest rigoureux",   desc: "Coûts d'exécution inclus, Sharpe/Sortino/Calmar/PBO/DSR calculés", to: "/app/backtest" },
  { Icon: ShieldCheck, label: "Audit zéro-trust",   desc: "Chaîne SHA-256 signée de chaque événement — vérifiable publiquement", to: "/audit" },
  { Icon: BookOpen,  label: "Academy — 40 articles", desc: "8 tracks : chartisme, quant, psychologie, risk, journal, macro, fiscalité", to: "/academy" },
];

const FAV_PRESETS = {
  "fx-major":     ["EURUSD", "GBPUSD", "USDJPY", "USDCHF", "AUDUSD", "USDCAD", "NZDUSD", "XAUUSD"],
  "multi-asset":  ["EURUSD", "GBPUSD", "XAUUSD", "BTCUSD", "ETHUSD", "SPY", "QQQ", "AAPL"],
  "starter":      ["EURUSD", "GBPUSD", "XAUUSD", "BTCUSD"],
};

export default function OnboardingWizard({ onClose }) {
  const [step, setStep] = useState(1);
  const [profile, setProfile] = useState(null);
  const [selectedAssets, setSelectedAssets] = useState(new Set());

  useEffect(() => {
    window.dispatchEvent(new Event("anatradex-onboarding-open"));
    return () => window.dispatchEvent(new Event("anatradex-onboarding-closed"));
  }, []);

  const finish = (assetsOverride) => {
    try {
      localStorage.setItem(LS_KEY, "1");
      if (profile) localStorage.setItem(LS_PROFILE, JSON.stringify(profile));
      const finalAssets = assetsOverride || Array.from(selectedAssets);
      if (finalAssets.length) localStorage.setItem(LS_FAVS, JSON.stringify(finalAssets.slice(0, 20)));
    } catch { /* noop */ }
    api.completeOnboarding().catch(() => {});
    onClose?.();
  };

  const skip = () => finish([]);

  const pickProfile = (p) => {
    setProfile(p);
    // Pre-populate assets from the profile's preset
    const preset = FAV_PRESETS[p.defaults.favSet] || FAV_PRESETS["multi-asset"];
    setSelectedAssets(new Set(preset));
    setStep(2);
  };

  const toggleAsset = (sym) => {
    setSelectedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(sym)) next.delete(sym);
      else if (next.size < 20) next.add(sym);
      return next;
    });
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="onboarding-wizard"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0b1420] shadow-2xl flex flex-col">
        {/* Skip button, always accessible top-right */}
        <button
          type="button"
          onClick={skip}
          className="absolute top-3 right-3 z-10 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/10"
          data-testid="onboarding-skip"
          title="Passer"
        >
          <X size={16} />
        </button>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 pt-4">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`h-1.5 rounded-full transition-all ${n === step ? "w-8 bg-accent-blue" : n < step ? "w-4 bg-up" : "w-4 bg-white/15"}`}
              data-testid={`onboarding-progress-${n}${n === step ? "-active" : ""}`}
            />
          ))}
        </div>

        <div className="px-8 py-6 flex-1 overflow-y-auto">
          {step === 1 && <StepProfile onPick={pickProfile} />}
          {step === 2 && (
            <StepAssets
              selected={selectedAssets}
              onToggle={toggleAsset}
            />
          )}
          {step === 3 && <StepTour onNavigate={() => finish()} />}
          {step === 4 && <StepMission onNavigate={() => finish()} />}
        </div>

        {/* Footer nav */}
        <div className="flex items-center justify-between gap-3 px-8 py-4 border-t border-white/10 bg-white/[0.02]">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="btn text-sm px-4 py-2"
              data-testid="onboarding-prev"
            >
              <ChevronLeft size={14} /> Retour
            </button>
          ) : <div />}
          {step === 2 && (
            <button
              type="button"
              onClick={() => setStep(3)}
              disabled={selectedAssets.size === 0}
              className="btn btn-primary text-sm px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="onboarding-next"
            >
              Continuer ({selectedAssets.size} actif{selectedAssets.size > 1 ? "s" : ""}) <ChevronRight size={14} />
            </button>
          )}
          {step === 3 && (
            <button
              type="button"
              onClick={() => setStep(4)}
              className="btn btn-primary text-sm px-4 py-2"
              data-testid="onboarding-next"
            >
              Continuer <ChevronRight size={14} />
            </button>
          )}
          {step === 4 && (
            <button
              type="button"
              onClick={() => finish()}
              className="btn btn-primary text-sm px-4 py-2"
              data-testid="onboarding-finish"
            >
              <Check size={14} /> Terminer & démarrer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------------ Step 1: profile ------------------------------------

function StepProfile({ onPick }) {
  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-accent-blue/10 border border-accent-blue/30 text-accent-blue text-[10px] uppercase tracking-widest mb-3">Étape 1 / 4</div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Quel est votre profil de trader ?</h2>
        <p className="text-sm text-white/60 mt-2">On adapte l&apos;interface, les timeframes par défaut et les préréglages de risque.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {TRADER_PROFILES.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPick(p)}
            className={`p-4 rounded-xl border text-left transition-all hover:scale-[1.02] hover:shadow-lg ${p.tint}`}
            data-testid={`onboarding-profile-${p.id}`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${p.tint} shrink-0`}>
                <p.Icon size={20} />
              </div>
              <div>
                <div className="font-heading text-lg font-semibold text-white">{p.label}</div>
                <div className="text-xs text-white/60 mt-0.5">{p.subtitle}</div>
                <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">
                  TF par défaut : <span className="text-white/80 font-mono">{p.defaults.timeframe}</span>
                  {p.defaults.killZones && <> · Kill-zones ON</>}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// ------------------------------------ Step 2: assets ------------------------------------

function StepAssets({ selected, onToggle }) {
  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-accent-blue/10 border border-accent-blue/30 text-accent-blue text-[10px] uppercase tracking-widest mb-3">Étape 2 / 4</div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Quels actifs suivez-vous ?</h2>
        <p className="text-sm text-white/60 mt-2">Vos favoris apparaîtront dans la Watchlist flottante. <span className="text-white/80 font-mono">{selected.size} / 20</span> sélectionnés.</p>
      </div>
      <div className="space-y-5">
        {ASSET_CATEGORIES.map((cat) => (
          <div key={cat.id}>
            <div className="text-xs uppercase tracking-widest text-white/50 mb-2 font-semibold">{cat.label}</div>
            <div className="flex flex-wrap gap-2">
              {cat.symbols.map((s) => {
                const isSel = selected.has(s);
                const canPick = isSel || selected.size < 20;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => onToggle(s)}
                    disabled={!canPick}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-mono font-semibold transition-all ${
                      isSel
                        ? "border-accent-blue/60 bg-accent-blue/15 text-accent-blue"
                        : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                    }`}
                    data-testid={`onboarding-asset-${s}`}
                  >
                    {isSel && <Check size={11} className="inline mr-1" />}
                    {s}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ------------------------------------ Step 3: feature tour ------------------------------------

function StepTour({ onNavigate }) {
  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-accent-blue/10 border border-accent-blue/30 text-accent-blue text-[10px] uppercase tracking-widest mb-3">Étape 3 / 4</div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Les 5 features clés de ANATRADEX</h2>
        <p className="text-sm text-white/60 mt-2">Cliquez pour explorer, ou continuez pour découvrir votre mission de testeur.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {FEATURE_TOUR.map((f, i) => (
          <Link
            key={i}
            to={f.to}
            onClick={onNavigate}
            className="p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-accent-blue/40 hover:bg-accent-blue/5 transition-all group"
            data-testid={`onboarding-tour-${i}`}
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-accent-blue/10 text-accent-blue border border-accent-blue/30 shrink-0 group-hover:scale-110 transition-transform">
                <f.Icon size={16} />
              </div>
              <div>
                <div className="font-heading text-base font-semibold text-white">{f.label}</div>
                <div className="text-xs text-white/60 mt-0.5">{f.desc}</div>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </>
  );
}

// ------------------------------------ Step 4: tester mission ------------------------------------

const MISSION_STEPS = [
  {
    Icon: UploadCloud,
    label: "1. Importez vos trades réels",
    desc: "CSV MT4 / MT5 / cTrader / Interactive Brokers — glisser-déposer, aperçu avant validation",
    to: "/app/import",
  },
  {
    Icon: FileText,
    label: "2. Générez votre rapport statistique",
    desc: "PBO, Deflated Sharpe, tests de permutation — calculés sur VOS données, chiffres reproductibles",
    to: "/app/import",
  },
  {
    Icon: BookOpen,
    label: "3. Lisez le guide d'interprétation",
    desc: "Comprendre les seuils : PBO, DSR, p-value — 5 minutes de lecture",
    to: "/app/guide",
  },
];

function StepMission({ onNavigate }) {
  return (
    <>
      <div className="text-center mb-6">
        <div className="inline-block px-3 py-1 rounded-full bg-up/10 border border-up/30 text-up text-[10px] uppercase tracking-widest mb-3">Étape 4 / 4 · Prêt à démarrer</div>
        <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Votre mission de testeur</h2>
        <p className="text-sm text-white/60 mt-2">Trois actions, dans cet ordre — c&apos;est tout ce qu&apos;on attend de vous. La checklist « Vos premiers pas » sur le tableau de bord suivra votre progression.</p>
      </div>
      <div className="space-y-3">
        {MISSION_STEPS.map((m, i) => (
          <Link
            key={i}
            to={m.to}
            onClick={onNavigate}
            className="flex items-start gap-3 p-4 rounded-xl border border-white/10 bg-white/[0.02] hover:border-up/40 hover:bg-up/5 transition-all group"
            data-testid={`onboarding-mission-${i}`}
          >
            <div className="p-2 rounded-lg bg-up/10 text-up border border-up/30 shrink-0 group-hover:scale-110 transition-transform">
              <m.Icon size={16} />
            </div>
            <div>
              <div className="font-heading text-base font-semibold text-white">{m.label}</div>
              <div className="text-xs text-white/60 mt-0.5">{m.desc}</div>
            </div>
          </Link>
        ))}
      </div>
      <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-accent-blue/25 bg-accent-blue/8 px-3.5 py-2.5 text-xs text-white/70" data-testid="onboarding-mission-feedback-note">
        <MessageSquare size={13} className="text-accent-blue shrink-0 mt-0.5" />
        <span>Un doute, un bug, une suggestion ? Le bouton <b className="text-white">« Feedback beta »</b> dans la barre latérale nous parvient directement — vos retours comptent double.</span>
      </div>
    </>
  );
}

/**
 * Hook: returns { show, close } — mount this in Dashboard and render the
 * <OnboardingWizard onClose={close} /> when show is true.
 * Sequencing: waits for the risk-ack modal; supports ?onboarding=1 replay.
 */
export function useOnboarding() {
  const [searchParams, setSearchParams] = useSearchParams();
  const replay = searchParams.get("onboarding") === "1";
  const [dismissed, setDismissed] = useState(false);
  const [completed] = useState(() => {
    try { return localStorage.getItem(LS_KEY) === "1"; }
    catch { return true; }
  });
  const [riskAcked, setRiskAcked] = useState(() => {
    try { return !!localStorage.getItem("qtr-risk-ack"); } catch { return true; }
  });

  useEffect(() => {
    const onAck = () => setRiskAcked(true);
    window.addEventListener("anatradex-risk-acked", onAck);
    return () => window.removeEventListener("anatradex-risk-acked", onAck);
  }, []);

  const show = riskAcked && !dismissed && (replay || !completed);
  const close = () => {
    setDismissed(true);
    if (replay) {
      searchParams.delete("onboarding");
      setSearchParams(searchParams, { replace: true });
    }
  };
  return { show, close };
}
